import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { Award, Gauge, MapPin, Clock, Share2, Copy, Loader2, Leaf, Trophy } from "lucide-react";
import { toast } from "sonner";

function scoreBand(eco) {
  if (eco >= 85) return { label: "Eco champion", color: "#2A9D8F", bg: "#E5DFD3", icon: Trophy };
  if (eco >= 70) return { label: "Smooth driver", color: "#2A9D8F", bg: "#EBE7E0", icon: Leaf };
  if (eco >= 50) return { label: "Good trip", color: "#C05A3A", bg: "#EBE7E0", icon: Award };
  return { label: "Easy on the throttle next time", color: "#A33B3B", bg: "#F9F8F6", icon: Award };
}

export function TripScoreBadge({ bookingId, onOpen }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/bookings/${bookingId}/trip-score`)
      .then(({ data }) => setScore(data))
      .catch(() => setScore({ available: false }))
      .finally(() => setLoading(false));
  }, [bookingId]);
  if (loading) return <Badge variant="outline" className="text-[10px]"><Loader2 size={10} className="mr-1 animate-spin" /> Trip score…</Badge>;
  if (!score?.available) return null;
  const band = scoreBand(score.eco_score);
  const Icon = band.icon;
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => onOpen?.(score)}
      className="gap-1.5 border-accent/40 hover:bg-accent/5"
      data-testid={`trip-score-badge-${bookingId}`}
    >
      <Icon size={12} className="text-accent" /> Trip score {score.eco_score}/100
    </Button>
  );
}

export default function TripScoreDialog({ open, onOpenChange, score }) {
  if (!score) return null;
  const band = scoreBand(score.eco_score);
  const Icon = band.icon;
  const shareUrl = `${window.location.origin}/trip/${score.booking_id}`;
  const shareText = `I just scored ${score.eco_score}/100 on my ${score.car_title} trip with ${score.platform_name}! ${score.distance_km}km · ${score.avg_speed_kmh}km/h avg.`;

  const copy = () => {
    navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
    toast.success("Copied to clipboard");
  };

  const tryNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "My RydeXpress trip score", text: shareText, url: shareUrl });
      } catch (e) {/* user cancelled */}
    } else {
      copy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="trip-score-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Your trip score</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Based on telemetry from your shared location during the trip.
          </DialogDescription>
        </DialogHeader>

        {/* Hero card — also the shareable design */}
        <div
          className="rounded-lg p-6 grain-overlay relative overflow-hidden"
          style={{ background: band.bg }}
          data-testid="trip-score-card"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">{score.platform_name}</div>
              <div className="font-display text-sm text-foreground/70 mt-1">{score.car_title}</div>
            </div>
            <Icon size={28} style={{ color: band.color }} />
          </div>
          <div className="mt-6 flex items-end gap-1.5">
            <div className="font-display text-6xl font-bold tracking-tighter leading-none" style={{ color: band.color }}>
              {score.eco_score}
            </div>
            <div className="font-display text-lg pb-1.5 text-foreground/60">/ 100</div>
          </div>
          <div className="text-sm font-display font-semibold mt-1" style={{ color: band.color }}>{band.label}</div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><MapPin size={11} /> Distance</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.distance_km} km</div>
            </div>
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><Clock size={11} /> Duration</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.duration_min} min</div>
            </div>
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><Gauge size={11} /> Avg speed</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.avg_speed_kmh} km/h</div>
            </div>
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><Gauge size={11} /> Max speed</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.max_speed_kmh} km/h</div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button onClick={tryNative} className="flex-1 bg-primary hover:bg-primary/90" data-testid="trip-score-share-btn">
            <Share2 size={14} className="mr-1.5" /> Share
          </Button>
          <Button onClick={copy} variant="outline" className="flex-1" data-testid="trip-score-copy-btn">
            <Copy size={14} className="mr-1.5" /> Copy link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
